import type { CSSProperties, JSX, KeyboardEvent, ReactNode } from "react";
import { useRef } from "react";
import { cx } from "../cx";

export interface SegmentedTabItem {
  id: string;
  label: ReactNode;
  /** Optional count, rendered in mono. */
  count?: number | string | undefined;
  /** The active scope — volt underline (a sanctioned selection moment). */
  active?: boolean | undefined;
}

export interface SegmentedTabsProps {
  items?: ReadonlyArray<SegmentedTabItem> | undefined;
  /** Fires with the tab id on click and on arrow-key moves. */
  onChange?: ((id: string) => void) | undefined;
  /** Accessible name for the tablist. */
  label?: string | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Studio scope tabs with mono counts — the primary way to partition a
 * high-volume data set (All 2153 / Need 4 / Done 2146). Selection is
 * controlled through each item's `active` flag; ArrowLeft/ArrowRight, Home,
 * and End move and select with a roving tabindex. Use the display `Tabs`
 * primitive on marketing surfaces; use this in the studio. Ported from
 * design/tarski-kit/components/studio/SegmentedTabs.jsx.
 */
export function SegmentedTabs({
  items = [],
  onChange,
  label,
  className,
  style,
}: SegmentedTabsProps): JSX.Element {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = items.findIndex((item) => item.active === true);
  const focusIndex = activeIndex >= 0 ? activeIndex : 0;

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const count = items.length;
    if (count === 0) {
      return;
    }
    let next: number;
    if (event.key === "ArrowRight") {
      next = (index + 1) % count;
    } else if (event.key === "ArrowLeft") {
      next = (index - 1 + count) % count;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = count - 1;
    } else {
      return;
    }
    event.preventDefault();
    const item = items[next];
    if (item === undefined) {
      return;
    }
    onChange?.(item.id);
    tabRefs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cx("tk-segmented-tabs", className)}
      style={style}
    >
      {items.map((item, index) => (
        <button
          key={item.id}
          ref={(element) => {
            tabRefs.current[index] = element;
          }}
          type="button"
          role="tab"
          aria-selected={item.active === true}
          tabIndex={index === focusIndex ? 0 : -1}
          className="tk-segmented-tabs__tab"
          onClick={() => onChange?.(item.id)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          {item.label}
          {item.count === undefined ? null : (
            <>
              {/* Explicit space keeps the accessible name "All 2153"; the
                  flex layout ignores the whitespace box. */}{" "}
              <span className="tk-segmented-tabs__count">{item.count}</span>
            </>
          )}
        </button>
      ))}
    </div>
  );
}
